<template>
  <div
    v-click-outside="() => menu.close()"
    class="menu-wrapper">
    <div class="control-buttons">
      <TabsSlider
        v-if="!isUsingPredictiveAvails"
        :is-disabled="disabled"
        :selected-tab-i-d="uiMode"
        :tabs="uiSwitchTabs"
        width="16rem"
        height="3rem"
        @selected-tab-changed="changeUiMode" />
      <button
        ref="menuToggle"
        class="more"
        :class="{ disabled }"
        :disabled="disabled"
        @click="() => menu.toggle()">
        <span>...</span>
      </button>
    </div>
    <Menu
      ref="menu"
      :anchor="menuToggle">
      <template v-if="!isLegacyMode">
        <MenuItem :label="$t('names.detailedForecast')">
          <ToggleButton
            :disabled="!shouldShowDetailedForecastToggle"
            :value="useDetailedForecast"
            @change="(value) => (useDetailedForecast = value)"></ToggleButton>
        </MenuItem>
        <MenuItem :label="$t('names.forecastOn')">
          <TabsSlider
            :selected-tab-i-d="selectedForecastMode"
            :tabs="forecastOnSwitchTabs"
            @selected-tab-changed="(x) => (selectedForecastMode = x.id as ForecastMode)" />
        </MenuItem>
        <MenuItem
          v-if="isLineItem && !isNew"
          :label="capitalize($t('actions.generateContendingLineItemsReport'))"
          :alignment="'center'">
          <HSButton
            :variant="'secondary'"
            :label="capitalize($t('actions.viewContendingLineItems'))"
            class-name="btn-xs"
            @click="() => emits('viewContendingLineItems')" />
        </MenuItem>
        <MenuItem
          :label="capitalize($t('messages.chartGroupActionDownload'))"
          :alignment="'center'">
          <div class="download-buttons">
            <HSButton
              :variant="'tertiary'"
              :label="capitalize($t('messages.chartGroupActionDownloadImage'))"
              class-name="btn-xs"
              @click="() => emits('exportGraph', 'IMAGE')" />
            <HSButton
              :variant="'tertiary'"
              :label="capitalize($t('messages.chartGroupActionDownloadCsv'))"
              class-name="btn-xs"
              @click="() => emits('exportGraph', 'CSV')" />
            <HSButton
              :variant="'tertiary'"
              :label="capitalize($t('messages.chartGroupActionDownloadExcel'))"
              class-name="btn-xs"
              @click="() => emits('exportGraph', 'EXCEL')" />
          </div>
        </MenuItem>
      </template>
      <MenuItem
        :label="$t('actions.downloadBreakdownSite')"
        alignment="center">
        <div class="download-buttons">
          <HSButton
            :icon-left="IconFileCSV"
            :variant="'secondary'"
            :label="'CSV'"
            class-name="btn-sm"
            @click="() => emits('downloadAvailsBreakdown', 'site', 'CSV')" />
          <HSButton
            :icon-left="IconFileXLS"
            :variant="'secondary'"
            :label="'XLSX'"
            class-name="btn-sm"
            @click="() => emits('downloadAvailsBreakdown', 'site', 'EXCEL')" />
        </div>
      </MenuItem>
      <MenuItem
        :label="$t('actions.downloadBreakdownScreen')"
        alignment="center">
        <div class="download-buttons">
          <HSButton
            :icon-left="IconFileCSV"
            :variant="'secondary'"
            :label="'CSV'"
            class-name="btn-sm"
            @click="() => emits('downloadAvailsBreakdown', 'screens', 'CSV')" />
          <HSButton
            :icon-left="IconFileXLS"
            :variant="'secondary'"
            :label="'XLSX'"
            class-name="btn-sm"
            @click="() => emits('downloadAvailsBreakdown', 'screens', 'EXCEL')" />
        </div>
      </MenuItem>
      <MenuItem
        v-if="!isLegacyMode"
        :alignment="'center'">
        <HSButton
          class-name="btn-xs"
          :variant="'tertiary'"
          :label="capitalize($t('actions.resetToDefaultView'))"
          @click="() => reset()" />
      </MenuItem>
    </Menu>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, Ref, ref, watch } from 'vue';

  import Menu from '@/common/components/controls/menu/Menu.vue';
  import MenuItem from '@/common/components/controls/menu/MenuItem.vue';
  import TabsSlider from '@/common/components/controls/TabsSlider.vue';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import HSButton from '@/common/components/ui/Button/Button.vue';
  import { IconFileCSV, IconFileXLS } from '@/common/icons/monochrome';
  import { LineItem, Package, Plan } from '@/common/models';
  import { mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  import ForecastMode from './ForcastMode.enum';
  import UIMode from './UIMode.enum';

  const DEFAULT_CONFIG = {
    useDetailedForecast: false,
    selectedForecastMode: ForecastMode.Inventory,
  } as const;

  interface IProps {
    disabled: boolean;
    entity: LineItem | Plan | Package;
  }

  const props = defineProps<IProps>();

  interface IEmits {
    (e: 'uiModeChanged', value: UIMode): void;
    (e: 'forecastModeChanged', value: ForecastMode): void;
    (e: 'useDetailedForecast', value: boolean);
    (e: 'downloadAvailsBreakdown', entity: 'screens' | 'site', format: 'EXCEL' | 'CSV'): void;
    (e: 'exportGraph', format: 'EXCEL' | 'CSV' | 'IMAGE'): void;
    (e: 'viewContendingLineItems'): void;
  }

  const emits = defineEmits<IEmits>();

  const uiSwitchTabs = [
    { id: UIMode.New, name: capitalize(i18n.global.t('actions.new')) },
    { id: UIMode.Legacy, name: capitalize(i18n.global.t('messages.legacy')) },
  ];

  const forecastOnSwitchTabs = [
    { id: ForecastMode.Inventory, name: capitalize(i18n.global.t('names.inventory')) },
    { id: ForecastMode.Audience, name: capitalize(i18n.global.t('names.audience')) },
  ];

  const menuToggle: Ref<HTMLElement> = ref();
  const menu: Ref<InstanceType<typeof Menu>> = ref();

  const uiMode = ref(UIMode.New);
  const useDetailedForecast = ref(false);
  const selectedForecastMode = ref(ForecastMode.Inventory);
  const isLegacyMode = computed(() => uiMode.value === UIMode.Legacy);
  const isLineItem = computed(() => props.entity instanceof LineItem);

  const { organizationSettings } = mapOrgAppState();

  const shouldShowDetailedForecastToggle = computed(
    () =>
      organizationSettings.externalAvailsUrl !== 'full-adserver-availability' &&
      !organizationSettings.uiEnablePredictiveAvails,
  );

  const isUsingPredictiveAvails = computed(() => organizationSettings?.uiEnablePredictiveAvails);

  const isNew = computed(() => props.entity.id === -1);

  function reset() {
    useDetailedForecast.value = DEFAULT_CONFIG.useDetailedForecast;
    selectedForecastMode.value = DEFAULT_CONFIG.selectedForecastMode;
  }

  function changeUiMode(tab) {
    menu.value.close();
    uiMode.value = tab.id as UIMode;
  }

  watch(
    isUsingPredictiveAvails,
    (isUsingPredictiveAvails: boolean) => (uiMode.value = isUsingPredictiveAvails ? UIMode.New : UIMode.Legacy),
  );
  watch(uiMode, (mode) => emits('uiModeChanged', mode));
  watch(useDetailedForecast, (value) => emits('useDetailedForecast', value));
  watch(selectedForecastMode, (mode) => emits('forecastModeChanged', mode));
</script>

<style scoped lang="scss">
  .menu-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: end;
  }

  .control-buttons {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    .button {
      padding: 0.5rem;
    }
  }

  .download-buttons {
    display: flex;
    justify-content: space-around;
    width: 100%;
  }

  .menu {
    margin-top: 4rem;
  }

  button {
    border: none;
    box-shadow: none;
    border-radius: 0.5rem;
    cursor: pointer;
    outline: none;
  }

  button.more {
    margin-left: 1rem;
    outline: none;
    cursor: pointer;
    border-radius: 0.5rem;
    transition: all 250ms;
    padding: 0.2rem 0.5rem;

    &.disabled {
      pointer-events: none;
    }

    &:hover,
    &.active {
      background: var(--actionsColor);
      color: var(--actionsColorReverse);

      transition: all 500ms;
    }

    span {
      writing-mode: vertical-lr;
      text-orientation: mixed;
      width: 1rem;
      height: 1rem;
      font-weight: bold;
      margin-left: -1px;
    }
  }
</style>
